import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { computed, ref, watch } from 'vue';
import { FilterEvent } from '../types/filterEvent';
import { useResetFilter } from '../composables/useFilterReset';
import EnFilterButtonBase from 'Components/datagrid/filterButtons/EnFilterButtonBase.vue';
import { useFilter } from '../composables/filterButtonComposable';
import { camelCaseToKebabCase } from 'Js/utils/stringUtils';
import { SelectionVariant } from '../types/selectionVariant';
import EnCheckBox from 'Components/inputs/EnCheckbox/EnCheckbox.vue';
import { useDropdownFilterButtonComposable } from 'Components/datagrid/composables/dropdownFilterButtonComposable';
import { MenuItem } from 'Components/primitives/menu';
import { useI18n } from 'vue-i18n';
/**
 * A filter button for a list of items.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnFilterButton',
    props: /*@__PURE__*/ _mergeModels({
        /**
         * The value that is applied when the filter is reset.
         * Can either be an item (string) or an index.
         */
        resetValue: { String, Number },
        /**
         * The options for the selection.
         */
        items: {
            type: Array,
            default: () => [],
        },
        /**
         * An Id for the component
         */
        id: String,
        /**
         * Strategy on how values can be selected
         */
        selectionVariant: {
            type: SelectionVariant,
            default: SelectionVariant.Multiple,
        }
    }, {
        "modelValue": { type: null },
        "modelModifiers": {},
        "openOnRender": { type: null },
        "openOnRenderModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(['update:modelValue', 'update:openOnRender', FilterEvent.Input, FilterEvent.FilterUpdated, FilterEvent.FilterReset], ["update:modelValue", "update:openOnRender"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const { t } = useI18n();
        const props = __props;
        const modelValue = _useModel(__props, "modelValue");
        const openOnRender = _useModel(__props, 'openOnRender');
        const menuIsVisible = ref(openOnRender.value ?? false);
        const emit = __emit;
        const { resetFilter } = useResetFilter(emit);
        const { getDisplayName, optionTitle, selectItem } = useDropdownFilterButtonComposable();
        const { returnFilterButtonId, } = useFilter(props, { emit }, modelValue, () => {
        });
        const filterValue = computed(() => {
            return getDisplayName(currentOption.value, props.selectionVariant, props.items);
        });
        const currentOption = computed({
            get() {
                return modelValue.value;
            },
            set(value) {
                const returnValue = (value || value === 0) ? value : props.resetValue;
                emit(FilterEvent.Input, returnValue);
                emit('update:modelValue', returnValue);
                emit(FilterEvent.FilterUpdated, value);
            },
        });
        const isMultipleSelected = computed(() => {
            return Array.isArray(currentOption.value) && currentOption.value?.length > 1 && currentOption.value.length != props.items.length - 1;
        });
        const shouldCloseOnContentClick = computed(() => props.selectionVariant === SelectionVariant.Single);
        function handleSelectItem(index) {
            currentOption.value = selectItem(props.items, props.selectionVariant, index, modelValue.value, menuIsVisible);
        }
        watch(menuIsVisible, (newValue) => {
            emit('update:openOnRender', newValue);
        });
        watch(openOnRender, (newValue) => {
            menuIsVisible.value = newValue;
        });
        function buildIdForElement(item) {
            return `${returnFilterButtonId(props.id)}${camelCaseToKebabCase('-Item-' + (item?.Id ?? 'All'))}`;
        }
        function shouldHaveCheckbox(item) {
            return props.selectionVariant === SelectionVariant.Multiple && item?.Id !== undefined;
        }
        function shouldHighlight(option) {
            // Do not mark element as active, since it will be always "the one".
            if (props.selectionVariant === SelectionVariant.Single) {
                return false;
            }
            return isSelected(option);
        }
        function isSelected(option) {
            if (Array.isArray(currentOption.value)) {
                return currentOption.value.find((id) => id === option.Id) !== undefined;
            }
            return currentOption.value === option.Id;
        }
        const __returned__ = { t, props, modelValue, openOnRender, menuIsVisible, emit, resetFilter, getDisplayName, optionTitle, selectItem, returnFilterButtonId, filterValue, currentOption, isMultipleSelected, shouldCloseOnContentClick, handleSelectItem, buildIdForElement, shouldHaveCheckbox, shouldHighlight, isSelected, EnFilterButtonBase, EnCheckBox, get MenuItem() { return MenuItem; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
